var _clampNode,
    _containsInlineContent,
    _hideNode,
    _removeMarginBottom,
    _restoreMarginBottom,
    _unclampNode,
    _unhideNode,
    clip,
    dom = require("enyo/dom");

/**
 * Clips multi-paragraph text and adds ellipsis to the last line as needed.
 *
 * @param   {Node}     node       The node that should be clipped
 * @param   {number}   maxHeight  The max height of the text
 * @param   {isRoot=}  isRoot     Whether the node is the root node of the text
 *                                that's being clipped.
 * @returns {boolean}  Whether the text was clipped
 * @public
 */
module.exports.clip = clip = function(node, maxHeight, isRoot) {
    var children,
        child,
        overflow,
        childMaxHeight,
        lineHeight,
        marginBottom,
        lineClamp,
        marginNode;

    // Bail if node doesn't overflow or doesn't have content
    if (node.scrollHeight <= maxHeight) {
        return false;
    }

    // Look for children that may need to be clipped
    children = Array.prototype.slice.call(node.children);

    // No children we need to clip the node directly
    if (!children.length) {
        overflow     = node.scrollHeight - maxHeight;
        lineHeight   = parseFloat(dom.getComputedStyleValue(node, "line-height"), 10);
        marginBottom = parseFloat(dom.getComputedStyleValue(node, "margin-bottom"), 10);
        lineClamp    = (node.offsetHeight + marginBottom - overflow) / lineHeight;
        lineClamp    = Math.floor(lineClamp);
        lineClamp === 0 ? _hideNode(node) : _clampNode(node, lineClamp);

        return true;
    }

    // Iterate over child nodes, hiding the ones that overflow completely and
    // cliping the one that overflows partially.
    while (children.length && node.scrollHeight > maxHeight) {
        child    = children.pop();
        overflow = node.scrollHeight - maxHeight;

        // Entire child can be hidden
        if (child.offsetHeight <= overflow) {
            _hideNode(child);
        }

        // Child contains children that might be child, let's traverse deeper
        // down the tree.
        else if (child.children.length && !_containsInlineContent(child)) {
            childMaxHeight = Math.max(0, child.offsetHeight - overflow);
            clip(child, childMaxHeight);
        }

        // Child's content can only be partially displayed.
        else {
            lineHeight   = parseFloat(dom.getComputedStyleValue(child, "line-height"), 10);
            marginBottom = parseFloat(dom.getComputedStyleValue(child, "margin-bottom"), 10);
            lineClamp    = Math.floor((child.offsetHeight + marginBottom - overflow) / lineHeight);
            lineClamp === 0 ? _hideNode(child) : _clampNode(child, lineClamp);
        }
    }

    // Remove margin at the bottom of the clipped text
    if (children.length && isRoot) {
        // Remove margin from last clipped child node, if one exists
        marginNode = node.querySelector("[clip-hidden],[clip-clamped]");

        // No clipped nodes found, we're done.
        if (!marginNode) {
            return true;
        }

        // If node is visually hidden, remove the bottom margin of it's previous
        // sibling.
        if (!marginNode.offsetHeight && marginNode.previousElementSibling) {
            marginNode = marginNode.previousElementSibling;
            marginNode.offsetHeight ? _removeMarginBottom(marginNode) : _hideNode(marginNode);
        }

        // Now check if we have a parent node that might need their margins
        // clipped.
        while (marginNode.parentNode !== node) {
            marginNode = marginNode.parentNode;
            marginNode.offsetHeight ? _removeMarginBottom(marginNode) : _hideNode(marginNode);

            // If node is visually hidden, remove the bottom margin of it's
            // previous sibling.
            if (!marginNode.offsetHeight && marginNode.previousElementSibling) {
                marginNode = marginNode.previousElementSibling;
                marginNode.offsetHeight ? _removeMarginBottom(marginNode) : _hideNode(marginNode);
            }
        }
    }

    return true;
};

/**
 * Undo text clipping.
 *
 * @param  {Node}  node  The node that should be unclipped
 * @public
 */
module.exports.unclip = function(node) {
    var nodes;

    // Bail if node doesn't have children
    if (!node.firstElementChild) {
        if (node.hasAttribute("clip-hidden")) {
            _unhideNode(node);
        }

        if (node.hasAttribute("clip-clamped")) {
            _unclampNode(node);
        }

        if (node.hasAttribute("clip-margin-removed")) {
            _restoreMarginBottom(node);
        }
    }

    // Reveal nodes that have been hidden
    nodes = Array.prototype.slice.call(node.querySelectorAll("[clip-hidden]"));

    while (nodes.length) {
        _unhideNode(nodes.pop());
    }

    // Unclamp nodes that have been clamped
    nodes = Array.prototype.slice.call(node.querySelectorAll("[clip-clamped]"));

    while (nodes.length) {
        _unclampNode(nodes.pop());
    }

    // Restore removed margins
    nodes = Array.prototype.slice.call(node.querySelectorAll("[clip-margin-removed]"));

    while (nodes.length) {
        _restoreMarginBottom(nodes.pop());
    }
};

/**
 * Checks if the supplied node contains any inline elements.
 *
 * @param    {Node}     node  The input node.
 * @returns  {boolean}  Whether inline elements were found.
 * @private
 */
_containsInlineContent = function(node) {
    var i;

    for (i = 0; i < node.children.length; i++) {
        if (dom.getComputedStyleValue(node.children[i], "display") === "inline") {
            return true;
        }
        i++;
    }

    return false;
};

/**
 * @param  {Node}  node  The input node.
 * @private
 */
_hideNode = function(node) {
    // Already hidden
    if (node.hasAttribute("clip-hidden")) {
        return;
    }

    node.style.display = "none";
    node.setAttribute("clip-hidden", "true");
};

/**
 * @param  {Node}  node  The input node.
 * @private
 */
_unhideNode = function(node) {
    if (node.hasAttribute("clip-hidden")) {
        node.style.display = null;
        node.removeAttribute("clip-hidden");
    }
};

/**
 * @param  {Node}    node   The input node
 * @param  {number}  lines  The maximum line count
 * @private
 */
_clampNode = function(node, lines) {
    // Already clamped
    if (node.hasAttribute("clip-clamped")) {
        return;
    }

    node.style.display         = "-webkit-box";
    node.style.webkitBoxOrient = "vertical";
    node.style.overflow        = "hidden";
    node.style.webkitLineClamp = lines;
    node.style.lineClamp       = lines;

    // Node.style.marginBottom    = "0px";
    node.setAttribute("clip-clamped", "true");
};

/**
 * @param  {Node}  node  The input node.
 * @private
 */
_unclampNode = function(node) {
    if (node.hasAttribute("clip-clamped")) {
        node.style.webkitBoxOrient = null;
        node.style.overflow        = null;
        node.style.webkitLineClamp = null;
        node.style.lineClamp       = null;

        // Node.style.marginBottom    = null;
        node.removeAttribute("clip-clamped");
    }
};

/**
 * @param  {Node}  node  The input node.
 * @private
 */
_removeMarginBottom = function(node) {
    var marginBottom;

    // Margin already removed
    if (node.hasAttribute("clip-margin-removed")) {
        return;
    }

    marginBottom            = node.style.marginBottom;
    node.style.marginBottom = "0px";
    node.setAttribute("clip-margin-removed", marginBottom || "true");
};

/**
 * @param  {Node}  node  The input node.
 * @private
 */
_restoreMarginBottom = function(node) {
    var marginBottom;

    if (node.hasAttribute("clip-margin-removed")) {
        marginBottom = node.getAttribute("clip-margin-removed");

        // Restore margin
        if (marginBottom !== "true") {
            node.style.marginBottom = marginBottom;
        }
        else {
            node.style.marginBottom = "";
        }

        node.removeAttribute("clip-margin-removed");
    }
};
